/**
 * Étape 1 — Orientation principale de l'exploitation.
 *
 * Choix de l'orientation métier (grandes cultures / élevage / maraîchage-horti…).
 * Détermine quelles sections d'infrastructures seront affichées par défaut
 * à l'étape 3 et calibre les recommandations CEE.
 */

import { motion } from 'framer-motion';
import { ArrowRight, Tractor } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import { ORIENTATIONS, type OrientationId } from './agri-config';
import { agriStepCounterLabel, useAgriStore } from './useAgriStore';

export function Step1Orientation() {
  const orientation = useAgriStore((s) => s.orientation);
  const setOrientation = useAgriStore((s) => s.setOrientation);
  const goNext = useAgriStore((s) => s.goNext);
  const counter = useAgriStore(agriStepCounterLabel);

  function onPick(id: OrientationId) {
    setOrientation(id);
  }

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
        {counter}
      </p>
      <h2 className="mt-2 h-section">
        Quelle est votre orientation principale&nbsp;?
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        Sélectionnez le type d'activité dominant. Nous adapterons les questions
        suivantes pour ne vous proposer que les aides CEE pertinentes pour votre
        exploitation.
      </p>

      <fieldset className="mt-6 border-0 p-0">
        <legend className="sr-only">Orientation de l'exploitation</legend>
        <div role="radiogroup" aria-label="Orientation de l'exploitation" className="grid gap-2.5 sm:grid-cols-2">
          {ORIENTATIONS.map((o) => {
            const Icon = o.icon;
            const selected = orientation === o.id;
            return (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onPick(o.id)}
                className={cn(
                  'group relative flex flex-col gap-3 overflow-hidden rounded-xl border-2 bg-white p-4 text-left transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                  selected
                    ? 'border-brand-500 bg-brand-50/70 shadow-[0_4px_24px_-8px_rgba(16,185,129,0.25)]'
                    : 'border-stone-200 hover:-translate-y-px hover:border-stone-300 hover:shadow-[0_4px_16px_-4px_rgba(10,10,10,0.08)]',
                )}
              >
                {selected && (
                  <motion.span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-50/0 via-brand-50/60 to-accent-50/40"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.25, ease: easeStripe }}
                  />
                )}

                <div className="relative flex items-start gap-3">
                  <span
                    className={cn(
                      'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors duration-150',
                      selected
                        ? 'bg-brand-100 text-brand-700'
                        : 'bg-stone-100 text-stone-600',
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'text-[14px] font-semibold leading-snug',
                        selected ? 'text-brand-900' : 'text-ink',
                      )}
                    >
                      {o.label}
                    </p>
                    <p className="mt-1 text-[12px] leading-relaxed text-stone-500">
                      {o.sublabel}
                    </p>
                  </div>
                  <ArrowRight
                    className={cn(
                      'h-4 w-4 shrink-0 transition-all duration-150',
                      selected
                        ? 'translate-x-0.5 text-brand-500'
                        : 'text-stone-300 group-hover:translate-x-0.5',
                    )}
                  />
                </div>

                {/* Sections révélées */}
                <div className="relative flex flex-wrap gap-1">
                  {o.reveals.serres && (
                    <Pill selected={selected} label="Serres" />
                  )}
                  {o.reveals.elevage && (
                    <Pill selected={selected} label="Élevage" />
                  )}
                  {o.reveals.stockage && (
                    <Pill selected={selected} label="Stockage" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </fieldset>

      <Button
        variant="brand"
        size="xl"
        disabled={orientation === null}
        onClick={goNext}
        className="mt-7 w-full gap-2"
      >
        Continuer
        <ArrowRight className="h-4 w-4" />
      </Button>

      <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-stone-400">
        <Tractor className="h-3 w-3" strokeWidth={1.8} />
        Questions calibrées sur les fiches CEE AGRI officielles
      </p>
    </div>
  );
}

function Pill({ selected, label }: { selected: boolean; label: string }) {
  return (
    <span
      className={cn(
        'rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide transition-colors',
        selected
          ? 'bg-white/80 text-brand-700 ring-1 ring-inset ring-brand-200/60'
          : 'bg-stone-100/80 text-stone-600',
      )}
    >
      {label}
    </span>
  );
}
